import { Trans } from "@lingui/macro";
import type { IPerms } from "@src/utils/components/permissions";
import cx from "classnames";
import { map } from "lodash";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { useSet } from "react-use";
import s from "./DataTable.module.css";

export type TColumnDef<T, S> = {
  /**
   * key in data row
   */
  key: string;

  /**
   * overrides key
   */
  render?: (row: T) => ReactNode;

  /**
   * shown in header
   */
  header: (() => ReactNode) | string;

  /**
   * used for formatting
   */
  type?: "numeric";

  sortable?: S;

  width?: string;

  withCan?: IPerms | IPerms[];

  align?: TAlign;
}[];

type TAlign = "left" | "center" | "right";

const alignToClassName = (val?: TAlign): string | undefined => {
  if (val === "center") return "text-center";
  if (val === "left") return "text-left";
  if (val === "right") return "text-right";
  return undefined;
};

// copied from graphql schema
enum SortOrder {
  Asc = "ASC",
  Desc = "DESC",
}

function nextSortValue(current: SortOrder = SortOrder.Desc) {
  const arr = [SortOrder.Asc, SortOrder.Desc];

  const currentIndex = arr.indexOf(current);
  const nextIndex = (currentIndex + 1) % arr.length;
  return arr[nextIndex];
}

function useSelection<T extends { id: string }>(
  allIds: T["id"][] = [],
  data: T[],
  initIds: T["id"][] = [],
) {
  const [selected, ops] = useSet(new Set<T["id"]>([]));

  const isCurrentPageSelected = (): boolean =>
    data.map((item) => item.id).every((id) => isSelected(id));

  const someSelectedOnCurrentPage = (): boolean =>
    data.map((item) => item.id).some((id) => isSelected(id));

  const isSelected = (id: T["id"]) => ops.has(id);

  const meta = useMemo(
    () => ({
      selectedRows: selected,
      allSelected: allIds.length === selected.size,
      noneSelected: selected.size === 0,
      someSelected: selected.size > 0,
      currentPage: {
        allSelected: isCurrentPageSelected(),
        someSelected: someSelectedOnCurrentPage(),
      },
    }),
    [allIds.length, selected.size, data],
  );

  const toggle = (id: T["id"]) => {
    ops.has(id) ? ops.remove(id) : ops.add(id);
  };

  const selectAll = () => {
    allIds.forEach(ops.add);
  };

  const toggleCurrentPage = () => {
    const currentPageIds = data.map((item) => item.id);

    if (isCurrentPageSelected()) {
      currentPageIds.forEach(ops.remove);
    } else {
      currentPageIds.forEach(ops.add);
    }
  };

  const clear = () => {
    ops.reset();
  };

  useEffect(() => {
    initIds.forEach((i) => {
      ops.has(i) ? ops.remove(i) : ops.add(i);
    });
  }, [initIds]);

  return {
    allIds,
    selected,
    meta,
    toggle,
    isSelected,
    selectAll,
    toggleCurrentPage,
    clear,
  };
}

export type TableSelection = ReturnType<typeof useSelection>;

type SortItem<C> = {
  column: C;
  order: SortOrder;
};

type Sort<C> = SortItem<C>[];

export function useTable<
  T extends { id: string; __subRows?: unknown[]; is_pinned?: boolean },
  S extends string,
>(
  data: T[],
  _columns: TColumnDef<T, S>,
  options?: {
    /**
     * whether to show 1st column as select boxes
     */
    selectable?: boolean;

    /**
     * array of ids - needed for select across table pages
     */
    allRowsIds?: T["id"][];

    /**
     * initial orderBy
     */
    orderedBy?: Sort<S>;
  },
  initialSelectedIds: T["id"][] = [],
) {
  type TData = Omit<T, "__subRows">;

  const selectable = options?.selectable;
  const sortOptions = options?.orderedBy;

  const selection = useSelection(options?.allRowsIds, data, initialSelectedIds);

  const [sorted, setSorted] = useState<Sort<S> | undefined>(sortOptions);

  const columns = [..._columns];
  if (selectable) {
    columns.unshift({ key: "__select" as any, header: "", width: "46px" });
  }

  const rows = useMemo(() => {
    return data.map((row) => {
      const cells = map(columns, (c) => {
        if (c.key === "__select")
          return {
            props: {
              key: c.key as keyof TData,
              className: cx("Polaris-DataTable__Cell"),
            },
            render() {
              return (
                <input
                  type="checkbox"
                  onChange={() => {
                    selection.toggle(row.id);
                  }}
                  checked={selection.isSelected(row.id)}
                />
              );
            },
          };

        return {
          props: {
            key: c.key as keyof TData,
            withCan: c.withCan,
            className: cx(
              "Polaris-DataTable__Cell",
              {
                "Polaris-DataTable__Cell--numeric": c.type === "numeric",
              },
              alignToClassName(c.align),
            ),
          },
          render(cmp?: (row: T) => ReactNode) {
            if (cmp) return cmp(row);
            if (c.render) return c.render(row);
            if (c.key in row) return row[c.key as keyof T];
            return null;
          },
        };
      });

      return {
        cells,
        /**
         * we have to pass type, because ts can't infer
         * generic type from another generic type
         */
        subRows() {
          return row.__subRows as T["__subRows"];
        },
        row,
        props: {
          key: row.id,
          className: cx("Polaris-DataTable__TableRow", {
            [s.selected]: selection.isSelected(row.id),
            [s.pinned]: row.is_pinned,
          }),
        },
      };
    });
  }, [data, columns]);

  const orderBy = useMemo(() => {
    if (sorted?.[0]) return sorted;
    return [];
  }, [sorted]);

  return {
    selection,
    orderBy,
    table: {
      props: {
        className: "Polaris-DataTable__Table",
      },
      rows,
      header: {
        cells: map(columns, (col) => {
          const thisSortedCol = sorted?.find((i) => i?.column === col.sortable);

          return {
            props: {
              key: col.key as keyof TData,
              className: cx(
                "Polaris-DataTable__Cell Polaris-DataTable__Cell--header",
                {
                  "Polaris-DataTable__Cell--numeric": col.type === "numeric",
                  "Polaris-DataTable__Cell--sortable Polaris-DataTable__Cell--sorted":
                    col.sortable,
                },
                alignToClassName(col.align),
              ),
              withCan: col.withCan,
              style: {
                width: col.width,
                whiteSpace: col.width ? "normal" : undefined,
              },
            },
            render() {
              if (col.key === "__select")
                return (
                  <input
                    type="checkbox"
                    ref={(el) => {
                      if (el) {
                        el.indeterminate =
                          selection.meta.currentPage.someSelected &&
                          !selection.meta.currentPage.allSelected;
                      }
                    }}
                    onChange={() => selection.toggleCurrentPage()}
                    checked={selection.meta.currentPage.allSelected}
                  />
                );

              function render() {
                return typeof col.header === "function"
                  ? col.header()
                  : col.header;
              }

              if (col.sortable) {
                return (
                  <button
                    type="button"
                    style={{
                      width: col.width,
                      whiteSpace: col.width ? "normal" : undefined,
                    }}
                    className="Polaris-DataTable__Heading"
                    onClick={() => {
                      if (!thisSortedCol) {
                        return setSorted([
                          { column: col.sortable!, order: SortOrder.Desc },
                        ]);
                      }
                      const no = nextSortValue(thisSortedCol.order);
                      setSorted([{ column: col.sortable!, order: no }]);
                    }}
                  >
                    {render()}
                    <span className="Polaris-DataTable__Icon">
                      {!thisSortedCol ? (
                        <svg viewBox="0 0 20 20" width="20" height="20">
                          <title>
                            <Trans>Sort</Trans>
                          </title>
                          <path
                            d="M13 8l-3-3-3 3h6zm-.1 4L10 14.9 7.1 12h5.8z"
                            fillRule="evenodd"
                          />
                        </svg>
                      ) : thisSortedCol?.order === SortOrder.Desc ? (
                        <svg viewBox="0 0 20 20" width="20" height="20">
                          <title>
                            <Trans>Sort descending</Trans>
                          </title>
                          <path d="M5 8l5 5 5-5z" fillRule="evenodd" />
                        </svg>
                      ) : thisSortedCol?.order === SortOrder.Asc ? (
                        <svg viewBox="0 0 20 20" width="20" height="20">
                          <title>
                            <Trans>Sort ascending</Trans>
                          </title>
                          <path d="M15 12l-5-5-5 5z" />
                        </svg>
                      ) : null}
                    </span>
                  </button>
                );
              }

              return render();
            },
          };
        }),
      },
    },
  };
}
